/* eslint-disable react-hooks/exhaustive-deps */
import {
  Avatar,
  Box,
  Button,
  FormHelperText,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetStateAuthors } from "../../app/slices/authorSlice";
import { showSnackbar } from "../../app/slices/commonSlice";
import { dateFormat, getBase64, getObjectDifference } from "../../app/utils";
import FileInput from "../../components/fileInput/fileInput";

const AuthorDetails = ({
  selectedAuthor,
  onSave,
  formValues,
  setFormValues,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  const hasChanges = useMemo(() => {
    if (!selectedAuthor) return true;
    const initialPayload = {
      name: selectedAuthor.NAME || "",
      biography: selectedAuthor.BIOGRAPHY || "",
      dateOfBirth: selectedAuthor.DATE_OF_BIRTH || "",
    };
    const currentPayload = {
      name: formValues.name || "",
      biography: formValues.biography || "",
      dateOfBirth: formValues.dateOfBirth || "",
    };
    return Object.keys(getObjectDifference(initialPayload, currentPayload)).length > 0 || Boolean(formValues.uploadedImage);
  }, [formValues, selectedAuthor]);
  const {
    post: { data, error },
    put: { data: putData, error: putError },
  } = useSelector(({ authors }) => authors);

  useEffect(() => {
    if (selectedAuthor) {
      setFormValues({
        name: selectedAuthor.NAME,
        biography: selectedAuthor.BIOGRAPHY,
        dateOfBirth: selectedAuthor.DATE_OF_BIRTH,
        profileImage: selectedAuthor.PROFILE_PICTURE,
      });
    }
  }, [selectedAuthor]);

  useEffect(() => {
    if (data || putData) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Author details updated successfully.",
          type: "success",
        }),
      );
      dispatch(resetStateAuthors(["post", "put"]));
      setIsEditing(false);
    }
  }, [data, putData]);

  useEffect(() => {
    if (error || putError) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            error?.message ||
            putError?.message ||
            "An error occurred while processing information.",
          type: "error",
        }),
      );
    }
  }, [error, putError]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    let inputValue = value;

    if (files?.length) {
      if (files[0]?.size > 5000000) {
        dispatch(
          showSnackbar({
            open: true,
            type: "error",
            message: "Upload should not exceed 5 megabytes in size",
          }),
        );
        return;
      }
      inputValue = files[0];
    }

    setFormValues((prevState) => ({ ...prevState, [name]: inputValue }));
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const validateForm = () => {
    let validationErrors = {};

    if (!formValues?.name?.trim()) {
      validationErrors.name = "Name is required";
    }

    // Validate Biography
    if (!formValues?.biography?.trim()) {
      validationErrors.biography = "Biography is required";
    }

    // Validate Date of Birth
    if (!formValues?.dateOfBirth) {
      validationErrors.dateOfBirth = "Date of birth is required";
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      let payload = formValues;
      if (formValues.uploadedImage) {
        payload.newImage = await getBase64(formValues.uploadedImage);
      }

      onSave(payload);
    }
  };

  const renderForm = () => (
    <Box
      sx={{
        marginTop: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <TextField
        label="Name"
        name="name"
        value={formValues.name}
        onChange={handleChange}
        fullWidth
        required
        error={!!errors.name}
        helperText={errors.name}
      />
      <TextareaAutosize
        name="biography"
        value={formValues.biography}
        onChange={handleChange}
        minRows={4}
        style={{
          width: "100%",
          padding: "10px",
          fontSize: "16px",
          borderRadius: "4px",
          borderColor: errors.biography ? "#f44336" : "#ccc",
        }}
        placeholder="Biography"
      />
      {errors.biography && (
        <FormHelperText error>{errors.biography}</FormHelperText>
      )}

      <TextField
        label="Date of Birth"
        name="dateOfBirth"
        type="date"
        value={formValues.dateOfBirth}
        onChange={handleChange}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          max: moment().format("YYYY-MM-DD"),
        }}
        fullWidth
        required
        error={!!errors.dateOfBirth}
        helperText={errors.dateOfBirth}
      />

      <Box>
        <FileInput
          onChange={handleChange}
          file={formValues?.uploadedImage}
          fileType={"image/*"}
          label={"Profile Image"}
          name="uploadedImage"
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        padding: 4,
        fontFamily: "Arial, sans-serif",
        maxWidth: 600,
        margin: "0 auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography variant="h4" gutterBottom>
        {selectedAuthor ? "Author Details" : "Register Author"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 3,
        }}
      >
        <Avatar
          alt={formValues.name}
          src={
            (formValues.uploadedImage &&
            formValues.uploadedImage instanceof File
              ? URL.createObjectURL(formValues.uploadedImage)
              : formValues.uploadedImage) || formValues?.profileImage
          }
          sx={{ width: 100, height: 100, border: "1px solid #ccc" }}
        />

        <Typography variant="body1">
          <strong>{formValues?.name || "Profile Image"}</strong>
        </Typography>
      </Box>

      {selectedAuthor && !isEditing ? (
        <Box>
          <Typography variant="body1">
            <strong>Name:</strong> {formValues.name}
          </Typography>
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            <strong>Biography:</strong> {formValues.biography}
          </Typography>
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            <strong>Date of Birth:</strong>{" "}
            {moment(formValues.dateOfBirth).format(dateFormat)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={toggleEdit}
            sx={{ marginTop: 3 }}
          >
            Edit Details
          </Button>
        </Box>
      ) : (
        renderForm()
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          marginTop: 2,
        }}
      >
        {(isEditing || !selectedAuthor) && (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleSubmit}
            style={{ marginRight: "5px" }}
            disabled={!hasChanges}
          >
            Save
          </Button>
        )}
        <Button color={"secondary"} variant={"contained"} onClick={handleClose}>
          {"Close"}
        </Button>
      </Box>
    </Box>
  );
};

export default AuthorDetails;
